// ─────────────────────────────────────────────
//  Cascade AI — Shell Tool
// ─────────────────────────────────────────────

import { exec } from 'node:child_process';
import { promisify } from 'node:util';
import type { ToolExecuteOptions } from '../types.js';
import { BaseTool } from './base.js';

const execAsync = promisify(exec);

export class ShellTool extends BaseTool {
  readonly name = 'shell';
  readonly description = 'Execute a shell command and return its output. Use for running scripts, compiling code, running tests, etc.';
  readonly inputSchema = {
    type: 'object',
    properties: {
      command: { type: 'string', description: 'The shell command to execute' },
      cwd: { type: 'string', description: 'Working directory (optional)' },
      timeout: { type: 'number', description: 'Timeout in milliseconds (default: 30000)' },
    },
    required: ['command'],
  };

  private allowlist: string[];
  private blocklist: string[];

  constructor(allowlist: string[] = [], blocklist: string[] = []) {
    super();
    this.allowlist = allowlist;
    this.blocklist = blocklist;
  }

  isDangerous(): boolean {
    return true;
  }

  async execute(input: Record<string, unknown>, options: ToolExecuteOptions): Promise<string> {
    const command = input['command'] as string;
    const cwd = (input['cwd'] as string | undefined) ?? this.workspaceRoot;
    const timeout = (input['timeout'] as number | undefined) ?? 30_000;

    this.validateCommand(command);

    try {
      // windowsHide: prevent a console window flash per command on Windows.
      // Output is piped (exec default), never inherited — it cannot write
      // over the live TUI.
      const { stdout, stderr } = await execAsync(command, { cwd, timeout, windowsHide: true });
      const out = [stdout, stderr].filter(Boolean).join('\n').trim();
      return out || '(no output)';
    } catch (err) {
      if (err instanceof Error && 'stdout' in err && 'stderr' in err) {
        const e = err as Error & { stdout: string; stderr: string; code: number };
        return `Exit ${e.code ?? 1}:\n${[e.stdout, e.stderr].filter(Boolean).join('\n').trim()}`;
      }
      throw err;
    }
  }

  private validateCommand(command: string): void {
    // Block obviously catastrophic patterns. This is defense-in-depth only —
    // a blocklist can never be exhaustive; the approval prompt is the real
    // gate. Patterns tolerate flag reordering and extra whitespace so trivial
    // variants like `rm  -fr  /` don't slip through.
    const builtinDangerous = [
      /\brm\s+(?:-\w+\s+)*-\w*[rf]\w*[rf]\w*\s+(?:\/|~|\$HOME)(?:\s|$)/, // rm -rf / , rm -fr ~
      />\s*\/dev\/[sh]d[a-z]/,
      /\bmkfs[.\s]/,
      /\bdd\s+.*\bof=\/dev\/[sh]d[a-z]/,
      /\bchmod\s+(?:-\w+\s+)*-?R?\s*777\s+\//,
      /:\(\)\s*\{\s*:\s*\|\s*:?\s*&\s*\}\s*;/, // fork bomb :(){ :|:& };:
    ];

    for (const pattern of builtinDangerous) {
      if (pattern.test(command)) {
        throw new Error(`Command blocked: matches dangerous pattern`);
      }
    }

    // User blocklist (substring match)
    for (const blocked of this.blocklist) {
      if (command.toLowerCase().includes(blocked.toLowerCase())) {
        throw new Error(`Command blocked by blocklist: "${blocked}"`);
      }
    }

    // User allowlist (if set, the FIRST token of the command must be an exact
    // match for an entry, and the command must contain no shell metacharacters).
    //
    // Previously this used `command.startsWith(a)`, which let `npm-foo` through
    // when `npm` was whitelisted. Token-level comparison closes that hole.
    if (this.allowlist.length > 0) {
      const firstToken = command.trimStart().split(/\s+/)[0] ?? '';
      const allowed = this.allowlist.some((a) => firstToken === a);
      if (!allowed) {
        throw new Error(
          `Command not in allowlist. First token "${firstToken}" must exactly ` +
          `match one of: ${this.allowlist.join(', ')}`,
        );
      }

      // Chaining metacharacters defeat the allowlist — block them when allowlist is active.
      const SHELL_METACHAR = /[;|`]|\$\(|&&|\|\|/;
      if (SHELL_METACHAR.test(command)) {
        throw new Error(
          `Command blocked: shell metacharacters (;, |, &&, ||, \`, $(...)) are not permitted ` +
          `when an allowlist is active because they allow chaining of arbitrary commands.`,
        );
      }
    }
  }
}
